import type { Immutable } from "immer";
import { produce } from "immer";
import { cloneDeep } from "lodash";
import type { ActionType } from "typesafe-actions";
import { createReducer } from "typesafe-actions";

import {
    clearTableSelection,
    createTableSelection,
    setSelectedRow,
    setSelectedRows,
    toggleSelectedRows,
    unsetSelectedRows,
    unsetSelectedRow,
} from "app/actions/tableActions";
import type { RootState } from "single-spa/store/types";

type Key = string | number;

type RecordType<T> = T extends object ? T : never;

export interface TableActionData<T = any> {
    tableKey: Key;
    selectKey: Key;
    records?: Array<RecordType<T>>;
    record?: RecordType<T>;
}

interface TableDataEntry<T = any> {
    isSelected: boolean;
    // @ts-ignore FIXME: strict migration
    data: TableActionData<T>["records"][number];
}

type TablesSelectionState = Record<Key, Record<Key, TableDataEntry>>;

const initialState: Immutable<TablesSelectionState> = {};

const tableActions = {
    createTableSelection,
    clearTableSelection,
    toggleSelectedRows,
    setSelectedRows,
    unsetSelectedRows,
    setSelectedRow,
    unsetSelectedRow,
};

function handleTableCreationIfMissing(state: TablesSelectionState, action: TableActionData) {
    const tableExists = !!state[action.tableKey];
    if (!tableExists) {
        state[action.tableKey] = {};
    }
}

function validateKeyValue(value: Key) {
    const typeOfValue = typeof value;
    if (typeOfValue !== "string" && typeOfValue !== "number") {
        throw new Error("Invalid key type, expected string or number, got " + typeof value);
    }

    return value;
}

export const tableSelectionReducer = createReducer<typeof initialState, ActionType<typeof tableActions>>(initialState)
    .handleAction(createTableSelection, (state, action) =>
        produce(state, (draft) => {
            handleTableCreationIfMissing(draft, action.payload);
        }),
    )
    .handleAction(clearTableSelection, (state, action) =>
        produce(state, (draft) => {
            delete draft[action.payload.tableKey];
        }),
    )
    .handleAction(toggleSelectedRows, (state, action) =>
        produce(state, (draft) => {
            handleTableCreationIfMissing(draft, action.payload);
            const { selectKey, tableKey, records = [] } = cloneDeep(action.payload);

            if (records.length === 0) {
                return;
            }

            const tableData = draft[tableKey];

            records.forEach((record) => {
                const recordKeyValue = validateKeyValue(record[selectKey]);

                if (!tableData[recordKeyValue]) {
                    tableData[recordKeyValue] = {
                        isSelected: true,
                        data: record,
                    };
                } else {
                    tableData[recordKeyValue].isSelected = !tableData[recordKeyValue].isSelected;
                }
            });
        }),
    )
    .handleAction(setSelectedRows, (state, action) =>
        produce(state, (draft) => {
            handleTableCreationIfMissing(draft, action.payload);
            const { selectKey, tableKey, records = [] } = cloneDeep(action.payload);

            if (records.length === 0) {
                return;
            }

            const tableData = draft[tableKey];

            records.forEach((record) => {
                const recordKeyValue = validateKeyValue(record[selectKey]);

                if (!tableData[recordKeyValue]) {
                    tableData[recordKeyValue] = {
                        isSelected: true,
                        data: record,
                    };
                } else {
                    tableData[recordKeyValue].isSelected = true;
                }
            });
        }),
    )
    .handleAction(unsetSelectedRows, (state, action) =>
        produce(state, (draft) => {
            handleTableCreationIfMissing(draft, action.payload);
            const { selectKey, tableKey, records = [] } = cloneDeep(action.payload);

            if (records.length === 0) {
                return;
            }

            const tableData = draft[tableKey];

            records.forEach((record) => {
                const recordKeyValue = validateKeyValue(record[selectKey]);

                if (!tableData[recordKeyValue]) {
                    tableData[recordKeyValue] = {
                        isSelected: false,
                        data: record,
                    };
                } else {
                    tableData[recordKeyValue].isSelected = false;
                }
            });
        }),
    )
    .handleAction(setSelectedRow, (state, action) =>
        produce(state, (draft) => {
            handleTableCreationIfMissing(draft, action.payload);
            const { selectKey, tableKey, record } = cloneDeep(action.payload);

            const tableData = draft[tableKey];
            const recordKeyValue = validateKeyValue(record[selectKey]);

            if (!tableData[recordKeyValue]) {
                tableData[recordKeyValue] = {
                    isSelected: true,
                    data: record,
                };
            } else {
                tableData[recordKeyValue].isSelected = true;
            }
        }),
    )
    .handleAction(unsetSelectedRow, (state, action) =>
        produce(state, (draft) => {
            handleTableCreationIfMissing(draft, action.payload);
            const { selectKey, tableKey, record } = cloneDeep(action.payload);

            const tableData = draft[tableKey];
            const recordKeyValue = validateKeyValue(record[selectKey]);

            if (!tableData[recordKeyValue]) {
                tableData[recordKeyValue] = {
                    isSelected: false,
                    data: record,
                };
            } else {
                tableData[recordKeyValue].isSelected = false;
            }
        }),
    );

export const selectTableSelectionState = (state: Partial<RootState>) => state?.tableSelection ?? {};
export const getTableSelectedItems =
    <T = any>(tableKey: string) =>
    (state: Partial<RootState>): T[] => {
        const tablesState = selectTableSelectionState(state);
        const table = tablesState[tableKey];

        if (!table) {
            return [];
        }

        // @ts-ignore FIXME: strict migration
        return Object.entries(table).reduce(
            (acc, [_, value]) => {
                if (value.isSelected) {
                    // @ts-ignore FIXME: strict migration
                    acc.push(value.data);
                }

                return acc;
            },
            [] as TableActionData["records"],
        );
    };
